/**
 * GetSidebarMenuQuery - Fetches sidebar menu tree from API
 * Server builds the menu based on user roles, tenant type, custom links.
 * React just renders what it receives.
 */

import type { IQuery, IQueryHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { SidebarMenuResponse, SidebarMenuItemData } from './sidebarMenu.types';
import { toCamelCase } from '@/utils/caseTransform';

// Recursively transform menu items from snake_case to camelCase
function transformMenuItems(items: any[]): SidebarMenuItemData[] {
  return items.map((item) => {
    const transformed = toCamelCase(item);
    if (transformed.children && Array.isArray(transformed.children)) {
      transformed.children = transformMenuItems(transformed.children);
    }
    return transformed as SidebarMenuItemData;
  });
}

// ============================================================================
// Query (Request)
// ============================================================================

export class GetSidebarMenuQuery implements IQuery<SidebarMenuResponse> {
  readonly type = 'GetSidebarMenuQuery' as const;
}

// ============================================================================
// Handler
// ============================================================================

export class GetSidebarMenuQueryHandler
  implements IQueryHandler<GetSidebarMenuQuery, SidebarMenuResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_query: GetSidebarMenuQuery): Promise<SidebarMenuResponse> {
    const response = await this.httpClient.get<BaseApiResponse<any>>('/api/v1/sidebar/menu');

    if (!response.succeeded) {
      throw new Error(response.message || 'Failed to fetch sidebar menu');
    }

    const data = toCamelCase(response.data);

    return {
      menuItems: transformMenuItems(data.menuItems ?? data.menu_items ?? []),
      logo: data.logo ?? '',
      collapsedLogo: data.collapsedLogo ?? '',
      notificationCount: data.notificationCount ?? 0,
      showNotificationBadge: data.showNotificationBadge ?? false,
    };
  }
}
